import { injectable, interfaces } from 'inversify';
import { action, computed, IObservableArray, makeObservable, observable, toJS } from 'mobx';
import { ProjectRole } from '../../models/userRole.model';
import { DatasetStatus } from '../datasets/datasetStatus.model';
import { ProjectStatus } from '../projects/projects.model';
import { AnnotationStatus, FREE_DRAW_TOOLS, IAnnotationProgress, IImage, IReviewProgress, ISegmentation, NoImageReason } from './annotations.interface';
import { ImageScopeName, IQuestionModel, validateQuestion } from './question.model';

export interface IHiddenSegmentation {
  segmentationId?: string;
  featureId: string;
}

export interface IAnnotationsStore {
  id?: string;
  image?: IImage;
  projectId: string;
  imageName: string;
  datasetName: string;
  datasetStatus: DatasetStatus;
  projectStatus: ProjectStatus;
  projectRole: ProjectRole;

  readonly segmentations: IObservableArray<ISegmentation>;
  readonly hiddenSegmentations: IObservableArray<IHiddenSegmentation>;
  selectedSegmentation?: ISegmentation;
  drawFocusFrameForId?: string;
  hoveredSegmentation?: string;
  invalidSegmentation?: ISegmentation;
  copiedSegmentation?: ISegmentation;

  readonly questions: IObservableArray<IQuestionModel>;
  readonly currentQuestions: IObservableArray<IQuestionModel>;

  isSelectedSegWithQuestions: boolean;
  isSelectedSegOfFreeDrawType: boolean;
  invalidQuestionsCount: number;
  allQuestionsValid: boolean;

  noImageReason: NoImageReason;
  annotationProgress: IAnnotationProgress;
  reviewProgress: IReviewProgress;
  annotationStatus?: AnnotationStatus;

  setImageName(imageName: string, datasetName: string, datasetStatus: DatasetStatus): void;
  setSegmentations(segmentations: ISegmentation[]): void;
  setProjectInfo(projectStatus: ProjectStatus, projectRole: ProjectRole): void;
  setHiddenSegmentations(hiddenFeatures: IHiddenSegmentation[]): void;
}

export namespace IAnnotationsStore {
  export const $: interfaces.ServiceIdentifier<IAnnotationsStore> = Symbol('ANNOTATIONS_STORE');
}

@injectable()
export class AnnotationsStore implements IAnnotationsStore {
  constructor() {
    makeObservable<AnnotationsStore, '_selectedSegmentation'>(this, {
      setSegmentations: action,
      setHiddenSegmentations: action,
      id: observable,
      projectId: observable,
      imageName: observable,
      datasetName: observable,
      datasetStatus: observable,
      projectRole: observable,
      image: observable,
      segmentations: observable,
      hiddenSegmentations: observable,
      hoveredSegmentation: observable,
      invalidSegmentation: observable,
      currentQuestions: observable,
      questions: observable,
      drawFocusFrameForId: observable,
      noImageReason: observable,
      annotationProgress: observable,
      reviewProgress: observable,
      annotationStatus: observable,
      projectStatus: observable,
      invalidQuestionsCount: computed,
      isSelectedSegWithQuestions: computed,
      isSelectedSegOfFreeDrawType: computed,
      _selectedSegmentation: observable,
      setImageName: action,
      setProjectInfo: action,
    });
  }

  setSegmentations(segmentations: ISegmentation[]): void {
    this.segmentations.replace(segmentations);
  }

  setHiddenSegmentations(hiddenFeatures: IHiddenSegmentation[]): void {
    this.hiddenSegmentations.replace(hiddenFeatures);
  }

  id?: string;

  projectId: string = '';

  imageName: string = '';

  datasetName: string = '';

  datasetStatus: DatasetStatus = 'None';

  projectRole: ProjectRole = ProjectRole.Annotator;

  image?: IImage;

  segmentations: IObservableArray<ISegmentation> = observable.array([], { deep: true });

  hiddenSegmentations: IObservableArray<IHiddenSegmentation> = observable.array([]);

  hoveredSegmentation?: string;

  copiedSegmentation?: ISegmentation;

  invalidSegmentation?: ISegmentation;

  currentQuestions: IObservableArray<IQuestionModel> = observable.array([], { deep: true });

  questions: IObservableArray<IQuestionModel> = observable.array([], { deep: true });

  drawFocusFrameForId?: string = undefined;

  noImageReason: NoImageReason = NoImageReason.Unknown;

  annotationProgress: IAnnotationProgress = {
    all: 0,
    totalImages: 0,
    you: 0,
  };

  reviewProgress: IReviewProgress = {
    allAccepted: 0,
    allRejected: 0,
    totalImages: 0,
    youAccepted: 0,
    youRejected: 0,
    awaitingReview: 0,
  };

  annotationStatus?: AnnotationStatus = undefined;

  projectStatus: ProjectStatus = ProjectStatus.Draft;

  get invalidQuestionsCount(): number {
    return this.segmentations.filter((s) => s.questions.some((q) => q.status.isValid === false)).length;
  }

  get isSelectedSegWithQuestions(): boolean {
    return this.selectedSegmentation !== undefined && this.selectedSegmentation.questions.length > 0;
  }

  get isSelectedSegOfFreeDrawType(): boolean {
    return this.selectedSegmentation !== undefined && FREE_DRAW_TOOLS.includes(this.selectedSegmentation.feature.featureType);
  }

  get allQuestionsValid(): boolean {
    this.questions.forEach((q) => validateQuestion(q));
    return this.questions.every((q) => q.status.isValid !== false);
  }

  private _selectedSegmentation: ISegmentation | undefined;
  public get selectedSegmentation(): ISegmentation | undefined {
    return this._selectedSegmentation;
  }

  public set selectedSegmentation(v: ISegmentation | undefined) {
    this._selectedSegmentation = v;
    const a = this.segmentations.find((x) => x.feature.id === this.selectedSegmentation?.feature.id)?.questions.map((q) => toJS(q));
    const b = this.questions.filter((q) => q.scopes.includes(ImageScopeName));
    this.currentQuestions.replace(a ?? b);

    if (this.drawFocusFrameForId !== this._selectedSegmentation?.feature.id) {
      this.drawFocusFrameForId = undefined;
    }
  }

  setImageName(imageName: string, datasetName: string, datasetStatus: DatasetStatus): void {
    this.imageName = imageName;
    this.datasetName = datasetName;
    this.datasetStatus = datasetStatus;
  }

  setProjectInfo(projectStatus: ProjectStatus, projectRole: ProjectRole): void {
    this.projectStatus = projectStatus;
    this.projectRole = projectRole;
  }
}
